import { v4 as uuid } from 'uuid';
import { MAX_INCLUSIONS } from '@ast-data/shared';
import { PEN_PRESETS } from '../media/sketch/constants';
import type { InclusionRow } from './inclusionRows';

/**
 * Where the Einschlüsse of a description meet the pens drawn on its sketches:
 * a Holzkohle line on the sketch SEEDS a Holzkohle row, the Anteil is what is
 * left to fill in. The same contract as the Schichten (see sketchStrata): it
 * adds a row, takes back one it added that nobody touched since, and never
 * removes a row typed by hand.
 */

interface PenInclusion {
    /** What the row is called — German, like every stored vocabulary word. */
    name: string;
    /** Words that already say the same, so no second row is added beside them. */
    aliases?: string[];
}

/**
 * The pens that name an Einschluss, keyed by PEN_PRESETS id. The names follow
 * INCLUSION_OPTIONS where it has the word, the pen's German label where not —
 * written out here, not translated: the row is data and stays German in an
 * English interface.
 */
const PEN_INCLUSIONS: Record<string, PenInclusion> = {
    HK: { name: 'Holzkohle', aliases: ['Holzkohleflitter'] },
    BL: { name: 'gebrannter Lehm', aliases: ['Hüttenlehm', 'Brandlehm'] },
    ZG: { name: 'Ziegelbruch', aliases: ['Ziegel'] },
    ST: { name: 'Steine', aliases: ['Stein'] },
    KE: { name: 'Keramik' },
    SX: { name: 'Silex', aliases: ['Feuerstein'] },
    TKN: { name: 'Tierknochen', aliases: ['Knochen'] },
    Mkn: { name: 'Menschenknochen', aliases: ['Knochen'] },
    GL: { name: 'Glas' },
    HO: { name: 'Holz' },
    Fe: { name: 'Eisen' },
    Bz: { name: 'Bronze', aliases: ['Buntmetall'] },
    Au: { name: 'Gold' },
    Ag: { name: 'Silber' },
    Cu: { name: 'Kupfer' },
    Zn: { name: 'Zink' }
};

function normalized(text: string): string {
    return text.trim().toLowerCase();
}

/** Whether a row already stands for this pen — seeded by it, or named alike. */
function answersTo(row: InclusionRow, penId: string): boolean {
    if (row.sourcePen === penId) {
        return true;
    }
    const pen = PEN_INCLUSIONS[penId];
    const name = normalized(row.inclusion);
    return [pen.name, ...(pen.aliases ?? [])].some(
        (word) => normalized(word) === name
    );
}

/**
 * A seeded row that still reads exactly as the seeding wrote it. Once someone
 * renamed it or entered an Anteil, it is the recorder's.
 */
function isUntouched(row: InclusionRow): boolean {
    const pen = row.sourcePen ? PEN_INCLUSIONS[row.sourcePen] : undefined;
    return (
        pen !== undefined &&
        row.inclusion.trim() === pen.name &&
        row.percentage.trim() === ''
    );
}

/**
 * Reconcile the Einschlüsse with the pens the sketches are drawn with. A pen
 * with no row gets one; an untouched seeded row whose pen is gone from every
 * sketch is dropped. A row deleted by hand comes back while its pen is still
 * drawn — the drawing says it is there.
 *
 * Returns the SAME array when nothing changed: the caller re-runs this whenever
 * the sketches change, and an identical list must not mark the form dirty.
 */
export function mergeSketchInclusions(
    rows: readonly InclusionRow[],
    penIds: readonly string[]
): InclusionRow[] {
    const drawn = new Set(penIds);
    const wanted = PEN_PRESETS.map((preset) => preset.id).filter(
        (penId) => drawn.has(penId) && PEN_INCLUSIONS[penId] !== undefined
    );

    const kept = rows.filter(
        (row) => !row.sourcePen || drawn.has(row.sourcePen) || !isUntouched(row)
    );

    const added: InclusionRow[] = [];
    for (const penId of wanted) {
        if (kept.length + added.length >= MAX_INCLUSIONS) {
            break;
        }
        if (kept.some((row) => answersTo(row, penId))) {
            continue;
        }
        added.push({
            id: uuid(),
            inclusion: PEN_INCLUSIONS[penId].name,
            percentage: '',
            sourcePen: penId
        });
    }

    if (kept.length === rows.length && added.length === 0) {
        return rows as InclusionRow[];
    }
    return [...kept, ...added];
}
